import { useEffect, useRef, useState } from 'react';

import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getIsInterestOnlyOffer } from '@helpers/FormalOffers/getIsInterestOnlyOffer';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { IconCheckCircleFilled, IconTimesDefault } from '@iwoca/icons';
import { Text } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import { FormattedCurrency } from '../../../../../../iwIntlProvider/FormattedCurrency';
import { ContinueButton } from '../ContinueButton/ContinueButton';
import { RepaymentCalculatorButton } from '../RepaymentCalculatorButton/RepaymentCalculatorButton';
import {
    FullTermDurationRow,
    FullTermRepaymentRow,
    InterestOnlyRepaymentRows,
    InterestRateRow,
    SummaryRow,
    WithdrawalAmountRow,
    WithdrawalFeesRow
} from '../SummaryRow/SummaryRow';

export const MobileLoanSummary = ({
    selectedOffer,
    drawdownAmount,
    monthlyRepayment,
    isLoading,
    isSingleOffer
}: {
    selectedOffer: TFormalOffer | undefined;
    drawdownAmount: number | undefined;
    monthlyRepayment: number | null;
    isLoading: boolean;
    isSingleOffer: boolean;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const startY = useRef(0);
    const panelRef = useRef<HTMLDivElement>(null);

    const isInterestOnly = selectedOffer
        ? getIsInterestOnlyOffer(selectedOffer)
        : false;

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent | TouchEvent) => {
            const target = e.target as HTMLElement;
            if (
                panelRef.current &&
                !panelRef.current.contains(target) &&
                // Exclude the modals
                !target.closest('[role="dialog"]')
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('pointerdown', handleClickOutside);
        return () =>
            document.removeEventListener('pointerdown', handleClickOutside);
    }, [isOpen]);

    return (
        <>
            <div
                ref={panelRef}
                className="fixed bottom-0 left-0 right-0 z-10 touch-none rounded-tl-l rounded-tr-l bg-primary-10 p-xl pt-m text-structure-100"
                onPointerDown={(e) => {
                    startY.current = e.clientY;
                }}
                onPointerUp={(e) => {
                    const delta = startY.current - e.clientY;
                    if (delta > 30) setIsOpen(true);
                    if (delta < -30) setIsOpen(false);
                }}
            >
                {isOpen && (
                    <>
                        <button
                            className="absolute right-m top-m"
                            onClick={() => setIsOpen(false)}
                            data-testid="close-mobile-summary"
                        >
                            <IconTimesDefault fontSize="24px" />
                        </button>

                        {selectedOffer?.personal_guarantee_proportion === 0 && (
                            <div className="mb-s mt-m inline-flex items-center gap-s rounded-xs border border-success-drk bg-success-lt p-s pr-m">
                                <IconCheckCircleFilled className="h-l w-l shrink-0 text-success-drk" />
                                <Text className="text-s font-bold text-structure-0">
                                    <FormattedMessage
                                        id="_.loan_summary.no_personal_guarantee"
                                        defaultMessage="No Personal Guarantee Required"
                                    />
                                </Text>
                            </div>
                        )}

                        <Text className="py-m pr-xl text-xl font-bold text-structure-100">
                            {isSingleOffer && selectedOffer ? (
                                <FormattedMessage
                                    id="_.loan_summary.single_offer_title_mobile"
                                    defaultMessage="Your loan summary over {months} months"
                                    values={{
                                        months: getDurationInMonths(
                                            selectedOffer.duration!
                                        )
                                    }}
                                />
                            ) : (
                                <FormattedMessage
                                    id="_.loan_summary.mobile_summary_heading"
                                    defaultMessage="Your loan summary"
                                />
                            )}
                        </Text>
                    </>
                )}

                {!isInterestOnly && (
                    <SummaryRow
                        label={
                            <FormattedMessage
                                id="_.loan_summary.min_monthly_repayment"
                                defaultMessage="Min. monthly repayment"
                            />
                        }
                        value={
                            <span className="text-xs font-bold text-structure-100">
                                <FormattedMessage
                                    id="_.loan_summary.min_monthly_repayment_mobile"
                                    defaultMessage="{amount} /month"
                                    values={{
                                        amount: (
                                            <span className="text-l">
                                                {monthlyRepayment !== null ? (
                                                    <FormattedCurrency
                                                        value={monthlyRepayment}
                                                    />
                                                ) : (
                                                    '£--.--'
                                                )}
                                            </span>
                                        )
                                    }}
                                />
                            </span>
                        }
                    />
                )}

                <div
                    className="overflow-hidden transition-[max-height] duration-300"
                    style={{ maxHeight: isOpen ? '500px' : '0px' }}
                    data-testid="summary-section"
                >
                    {isInterestOnly && (
                        <div className="flex flex-col gap-s pb-m">
                            <InterestOnlyRepaymentRows
                                selectedOffer={selectedOffer}
                                drawdownAmount={drawdownAmount}
                            />
                        </div>
                    )}
                    <div
                        className={`mt-m flex flex-col gap-s border-t border-secondary-20 pb-m pt-m ${isLoading ? 'opacity-50' : ''}`}
                    >
                        <WithdrawalAmountRow drawdownAmount={drawdownAmount} />
                        <InterestRateRow
                            selectedOffer={selectedOffer}
                            drawdownAmount={drawdownAmount}
                        />
                        <WithdrawalFeesRow selectedOffer={selectedOffer} />
                    </div>

                    <RepaymentCalculatorButton
                        selectedOffer={selectedOffer}
                        drawdownAmount={drawdownAmount}
                    />

                    <div
                        className={`flex flex-col gap-s py-l ${isLoading ? 'opacity-50' : ''}`}
                    >
                        <FullTermDurationRow
                            selectedOffer={selectedOffer}
                            drawdownAmount={drawdownAmount}
                        />
                        <FullTermRepaymentRow
                            selectedOffer={selectedOffer}
                            drawdownAmount={drawdownAmount}
                        />
                    </div>
                </div>

                <div className="pt-l">
                    {isOpen ? (
                        <ContinueButton
                            selectedOffer={selectedOffer}
                            drawdownAmount={drawdownAmount}
                        />
                    ) : (
                        <button
                            className="h-[48px] w-full rounded-full border-2 border-structure-100 text-structure-100"
                            onClick={() => setIsOpen(true)}
                        >
                            <FormattedMessage
                                id="_.loan_summary.open_summary_button"
                                defaultMessage="Continue to summary"
                            />
                        </button>
                    )}
                </div>
            </div>

            {/* Bottom spacer so the content behind the drawer isn't hidden */}
            <div className="h-[160px]" />
        </>
    );
};
